
/**
 * This provides methods that can be called globally across different
 * components on both app.js and cms.js
 * 
 * Any methods defined here MUST BE DOCUMENTED. All method names should start
 * with "mod" before their method names. This is to easily indicate that this
 * is coming from the ModHelpers.
 * 
 * IDEs support JSDOCs, so install it on your preferred IDE. Once installed
 * this will provide information of the methods for your Intellisense
 * 
 * Please follow the JSDocs Format
 * https://jsdoc.app/
 * 
 * @mixin
 */
const ModHelpers = {
    methods: {
      
      /**
       * Calculates the total amount of price and quantity
       * @param {number} price
       * @param {number} qty
       * @return {number} | {string}
       */      
      modCalculateTotalAmount(price, qty, asDollar) {
        let total = parseFloat(price) * parseFloat(qty) 
        
        if (asDollar) {
          return this.modFormatDollarAmount(total)
        }
        
        return total 
      },
      
      /**
       * Creates a 2 decimal places from a number
       * @param {number} value
       * @return {string}
       */
      modFormatAmount (value) {
        let amount = parseFloat(value)
        
        return amount
          .toFixed(2)
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      },
      
      /**
       * Creates a 2 decimal places from a number and adds the $ symbol
       * @param {number} value
       * @return {string}
       */
      modFormatDollarAmount (value) {
        return '$' + this.modFormatAmount(value)
      },
      
      /**
       * This allows property names to be humanize or capitalize the 1st letter 
       * of the word. This is commonly used for property names to automatically
       * set them as labels
       * @param {string} fieldName
       * @return {string}
       */
      modHumanizeProperty (fieldName) {
        return fieldName.replace(/^[\s_]+|[\s_]+$/g, '')
          .replace(/[_\s]+/g, ' ')
          .replace(/^[a-z]/, function(value) { 
            return value.toUpperCase() 
          })
      },
      
      /**
       * This method capitalize the first character of the string and replaces 
       * the underscore into a space character. This is commonly used when
       * converting attributes/properties into human readable format for
       * input fields
       * @param {string} field
       * @return {string}
       */
      modCapitalize(field) {
        let string = field.charAt(0).toUpperCase() + field.slice(1)
        return string.replace('_', ' ')
      },
      
      /**
       * This method converts the email string into an email url which is used
       * on hrefs
       * @param {string} email
       * @return {string}
       */
      modEmailUrl(email) {
        return 'mailto:' + email
      },
      
      /**
       * This method converts the phone string into an phone url which is used
       * on hrefs
       * @param {string} phone
       * @return {string}
       */
      modPhonelUrl(phone) {
        let string = phone.replace(/[^0-9\-]/, '')
        return 'tel:' + string
      },
      
      /**
       * This method validates if an email is valid. But still do BE validations
       * as javascript can be disabled on the browser
       * @param {string} email
       * @return {boolean}
       */
      modValidateEmail(email) {
        const regex = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return regex.test(email);
      },
      
      /**
       * This method checks if the object is empty or bit
       * @param {object} object
       * @return {boolean}
       */
      modIsObjectEmpty(object) {
        if (Object.keys(object).length === 0) {
          return true
        }
        
        return false
      }
      
    }
}

export default ModHelpers;